<template>
    <button
        ref="menuButtonRef"
        type="button"
        @click="isMenuOpen = !isMenuOpen"
        :class="[
            'relative z-[52] w-8 h-8 rounded-lg flex items-center justify-center transition-all border active:scale-95',
            isMenuOpen
                ? 'bg-white/15 text-white border-white/20'
                : 'bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border-white/10 hover:border-white/20'
        ]"
        aria-label="Menu"
        :aria-expanded="isMenuOpen"
    >
        <MoreVertical class="w-4 h-4" />
    </button>

    <!-- Flyout Menu -->
    <Teleport v-if="isMenuOpen" to="body">
        <!-- 1000+ band on purpose: the join cards' "BEST DEAL" ribbon escapes its parent at
             z-index 100 and lives in shared code, so the backdrop clears it by an order of
             magnitude rather than the ribbon being lowered for everyone. -->
        <div class="fixed inset-0 z-[1000] bg-black/20 backdrop-blur-md" @click="isMenuOpen = false" />

        <div
            ref="menuRef"
            class="fixed z-[1010] w-64 bg-zinc-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden"
            :style="menuPanelStyle"
        >
            <template v-if="isUserLoggedIn">
                <div class="p-2">
                    <KebabMenuItem
                        :type="'a'"
                        :icon="Home"
                        label="Browse more Naughty America"
                        :href="route(routePrefix + '.main.home')"
                        @click="isMenuOpen = false"
                    />
                    <KebabMenuItem
                        :type="'a'"
                        :icon="Settings"
                        label="Account Settings"
                        :href="route('members2.account.profile')"
                        @click="isMenuOpen = false"
                    />
                    <KebabMenuItem
                        :type="'a'"
                        :icon="HelpCircle"
                        label="Help & Support"
                        href="https://support.latouraineinc.com"
                        @click="isMenuOpen = false"
                    />

                    <!-- Deliberately leaves the menu open: the confirmation is this row
                         turning into "Reported", not a modal. -->
                    <KebabMenuItem
                        v-if="canReport"
                        :icon="hasReported ? CheckCircle2 : Flag"
                        :label="reportLabel"
                        :variant="hasReported ? 'default' : 'danger'"
                        @click="reportClip"
                    />
                    <p v-if="reportError" class="px-3 pb-1 text-red-400 text-xs">
                        {{ reportError }}
                    </p>
                </div>

                <KebabMenuDivider />

                <div class="p-2">
                    <KebabMenuItem
                        :type="'a'"
                        :icon="LogOut"
                        label="Sign Out"
                        :href="route('members2.auth.logout')"
                        variant="danger"
                        @click="isMenuOpen = false"
                    />
                </div>
            </template>

            <template v-else>
                <div class="p-2">
                    <KebabMenuItem
                        :icon="LogIn"
                        label="Join"
                        variant="primary"
                        @click="goToJoin"
                    />
                    <KebabMenuItem
                        :type="'a'"
                        :icon="User"
                        label="Sign In"
                        :href="route('members2.auth.login', { dest: currentUrl })"
                    />
                </div>

                <KebabMenuDivider />

                <div class="p-2">
                    <KebabMenuItem
                        :type="'a'"
                        :icon="Home"
                        label="Browse more Naughty America"
                        :href="route(routePrefix + '.main.home')"
                        @click="isMenuOpen = false"
                    />
                    <KebabMenuItem
                        :type="'a'"
                        :icon="HelpCircle"
                        label="Help & Support"
                        href="https://support.latouraineinc.com"
                        @click="isMenuOpen = false"
                    />
                </div>
            </template>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { CheckCircle2, Flag, HelpCircle, Home, LogIn, LogOut, MoreVertical, Settings, User } from 'lucide-vue-next'
import KebabMenuItem from '@/Components/Reels/KebabMenuItem.vue'
import KebabMenuDivider from '@/Components/Reels/KebabMenuDivider.vue'
import { useClipAnalytics } from '@/composables/useClipAnalytics'
import { useClipReport } from '@/composables/useClipReport'

const props = defineProps({
    isUserLoggedIn: {
        type: Boolean,
        default: false
    },
    // Drives every prefix-able route in here - www / tour / www3 / members2 - so the
    // menu keeps the visitor on the domain they arrived on.
    routePrefix: {
        type: String,
        default: 'www'
    },
    // The clip on screen, forwarded by ClipsSidebar, or null when the menu is mounted without
    // clip context. Its id targets reports; its slug rides the join link as custom7 so
    // the sale is attributable to the clip, not just the placement.
    currentClip: {
        type: Object,
        default: null
    },
})

const { trackJoinCtaClicked } = useClipAnalytics({ routePrefix: props.routePrefix })

const isMenuOpen = ref(false)
const menuRef = ref(null)
const menuButtonRef = ref(null)
const menuPanelStyle = ref({})

const currentUrl = computed(() => {
    if (typeof window !== 'undefined' && window.location) {
        return window.location.pathname + window.location.search
    }

    return ''
})

// The menu outlives the clip - it stays mounted while the feed swipes - so the reported
// state is keyed on the clip inside the composable.
const {
    canReport,
    reportLabel,
    reportError,
    hasReported,
    reportClip,
} = useClipReport({
    clip: () => props.currentClip,
    routePrefix: props.routePrefix,
    isUserLoggedIn: () => props.isUserLoggedIn,
})

// getBoundingClientRect() is not a reactive source, so this cannot be a computed: it
// would cache the first measurement and the panel would never follow the button on a
// resize or an orientation change. Recompute on open and while open.
const updateMenuPanelStyle = () => {
    if (!menuButtonRef.value) return

    const rect = menuButtonRef.value.getBoundingClientRect()

    menuPanelStyle.value = {
        top: `${rect.bottom + 8}px`,
        right: `${window.innerWidth - rect.right}px`,
    }
}

const goToJoin = () => {
    isMenuOpen.value = false

    const joinUrl = route('www.join.signUp', {
        custom6: 'clipsKebabJoin',
        ...(props.currentClip?.slug ? { custom7: props.currentClip.slug } : {}),
    })

    // Fire-and-forget, immediately before the redirect - clip context comes from the
    // currentClip prop (null until a clip mounts); `cta_source` carries the entry point.
    trackJoinCtaClicked('clipsKebabJoin', props.currentClip ?? null, joinUrl)

    window.location.href = joinUrl
}

const handleClickOutside = (event) => {
    if (
        menuRef.value &&
        !menuRef.value.contains(event.target) &&
        menuButtonRef.value &&
        !menuButtonRef.value.contains(event.target)
    ) {
        isMenuOpen.value = false
    }
}

const handleEscape = (event) => {
    if (event.key === 'Escape') {
        isMenuOpen.value = false
    }
}

const removeListeners = () => {
    document.removeEventListener('mousedown', handleClickOutside)
    document.removeEventListener('keydown', handleEscape)
    window.removeEventListener('resize', updateMenuPanelStyle)
    window.removeEventListener('orientationchange', updateMenuPanelStyle)
}

watch(isMenuOpen, (isOpen) => {
    if (isOpen) {
        updateMenuPanelStyle()
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)
        window.addEventListener('resize', updateMenuPanelStyle)
        window.addEventListener('orientationchange', updateMenuPanelStyle)
    } else {
        removeListeners()
    }
})

onUnmounted(removeListeners)
</script>
